// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.DynamicThresholdField.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

import React, { Component } from 'react';
import DropDown from 'share/components/controls/Izenda.DropDown';
import Popup from 'share/components/controls/CommonPopup/Izenda.Popup';
import GridLayout from 'share/components/controls/Izenda.GridLayout';
import connect from 'share/enhancers/Izenda.Connect';
import ReportPartUtils from 'share/utils/Izenda.ReportPartUtils';
import commonDataStore from 'logic/Izenda.ReportDesignerCommonDataStore';
import FormatDataType from 'logic/models/Izenda.FormatDataType';
import { OPERATOR_GROUP } from 'logic/data/Izenda.FilterDetailsConfigConstants';
import { DATA_TYPES } from 'logic/data/Izenda.ReportDesignerConstants';
import { observable, action } from 'mobx';
import { EMPTY_FUNCTION } from 'logic/data/Izenda.ReportPartFieldConstants';
import { ContainerName } from 'logic/data/Izenda.ReportPartChartConstants';
import { get, concat, filter, isEmpty, first, find, getOr } from 'lodash/fp';

@connect()
class DynamicThresholdField extends Component {
  @observable functions: Array<any> = [];
  @observable field: any = {};
  @observable functionId: string = null;
  @observable disableFunctions = false;
  @observable format = new FormatDataType();
  @observable formats: Array = [];
  @observable fieldContainerName: string = ContainerName.VALUES;

  constructor(props) {
    super(props);
    const {
      data: { thresholdFieldElement, functionId },
      reportPartContent,
      dataTree,
      fieldContainerName
    } = this.props;

    this.fieldContainerName = fieldContainerName || ContainerName.VALUES;
    const field = get('field', thresholdFieldElement);
    if (thresholdFieldElement && field.visible) {
      this.fieldId = field.fieldId;
      const querySourceId = get(
        'querySourceDetail.id',
        find(item => item.querySourceDetail.name === field.sourceAlias, dataTree)
      );
      this.querySourceId =
        field.relationshipId && !String.isEmptyGuid(field.relationshipId) ? field.relationshipId : querySourceId;
      this.format.update(get('properties.dataFormattings.format', thresholdFieldElement));
      this.dataType = field.dataFieldType;
      this.functionId = functionId;
      this.disableFunctions = field.isRunningField;
    }
    const hasAggregatedFunction = get('hasAggregatedFunction', field);
    const isRunningField = get('isRunningField', field);
    this.metricContainer = get(reportPartContent.getMetricContainer(), reportPartContent);

    !hasAggregatedFunction && !isRunningField && this.getFunctionList();
    this.getFormats(this.disableFunctions);
  }

  /**
   * On closing popup
   */
  onClosing = (callBack, type) => {
    if (type === 'accept') {
      const functionData = find({ id: this.functionId }, this.functions);
      const { fieldId, functionId, format, querySourceId } = this;
      this.props.onAccept({
        fieldId,
        functionId,
        functionData,
        format,
        querySourceId
      });
    } else {
      this.props.onReject();
    }
  };

  onChangeFunction = e => {
    this.functionId = e.value;
  };

  /**
   * Onchange format value
   * @param {any} value
   * @param {any} selectedFormat
   *
   */
  onChangeFormat = ({ label }, selectedFormat: any) => {
    const formatValue = {
      formatId: getOr(null, 'customId', selectedFormat),
      format: isEmpty(label) ? null : getOr(null, 'format', selectedFormat),
      jsFormatString: getOr(null, 'jsFormatString', selectedFormat),
      groupBy: getOr(null, 'jsFormatString', selectedFormat),
      formatDataType: getOr(null, 'jsFormatString', selectedFormat)
    };

    this.format.update(formatValue);
  };

  /**
   * On change field
   */
  onChangeField = ({ label, additionalValue }) => {
    this.fieldId = additionalValue.id;
    this.name = label;
    this.field = additionalValue;
    const { dataTree } = this.props;
    const { querySourceDetail } = find(item => item.field === additionalValue, dataTree);
    this.querySourceId = querySourceDetail.id;
    if (this.dataType !== additionalValue.izendaDataType) {
      this.dataType = additionalValue.izendaDataType;
      this.functionId = null;
    }
    const disableFunctions = additionalValue.isRunningField;
    this.disableFunctions = disableFunctions;

    this.getFunctionList(this.disableFunctions, true);
    this.format.reset();
    this.getFormats(this.disableFunctions);
  };

  /**
   * Get list of function based on data type
   * @param {Boolean} disableFunctions
   * @param {Boolean} fromChangeField
   */
  @action
  getFunctionList(disableFunctions: boolean, fromChangeField: boolean) {
    const { reportPartContent } = this.props;
    const dataType = this.dataType;
    if (this.dataType && !disableFunctions) {
      ReportPartUtils.getFunction(0, this.dataType).then(result => {
        this.functions = this.setFunctionList(result);
        const defaultFunction = reportPartContent.getDefaultFunctionFormat({ name: this.fieldContainerName }, dataType);
        const functionData = find({ name: defaultFunction.FUNCTION.NAME }, result);
        !isEmpty(this.functions) && functionData && fromChangeField && (this.functionId = functionData.id);
      });
    } else {
      this.functionId = null;
      this.functions = [];
    }
  }

  /**
   * Get format by numeric
   * @param {boolean} isRunningField
   * @returns {Array<any>}
   */
  @action
  getFormats(isRunningField: boolean): Array<any> {
    const { reportPartContent } = this.props;

    return commonDataStore.loadReportFieldDataFormat(DATA_TYPES.NUMERIC).then(resultFormats => {
      this.formats = ReportPartUtils.filterFormatForField(
        reportPartContent,
        resultFormats,
        isRunningField,
        this.fieldContainerName
      );
    });
  }

  /**
   * Set default function for fields
   */
  setFunction() {
    !isEmpty(this.functions) && !this.functionId && (this.functionId = get('id', first(this.functions)));
  }

  /**
   * Set correct function list for field
   * @param {Array} result
   */
  setFunctionList(result: Array<any>) {
    const { reportPartContent } = this.props;
    result = concat([EMPTY_FUNCTION], result);

    if (!reportPartContent.checkedCompatibleFunction) {
      return result;
    }

    return filter(
      reportPartContent.checkedCompatibleFunction.bind(reportPartContent, this.metricContainer, {
        field: this.field
      }),
      result
    );
  }

  /**
   * Get value for each item in dropdown
   * @param {Object} field
   */
  getValueField(field: any) {
    return `${field.id}_${field.querySourceDetail.id}`;
  }

  /**
   * Get selected value
   */
  getValue() {
    return `${this.fieldId}_${this.querySourceId}`;
  }

  render() {
    const { localization, dataTree } = this.props;

    return (
      <Popup visible onClosing={this.onClosing} autoClose={false} type="none">
        <Popup.Header title={localization.getText('THRESHOLDS_TITLE_POPUP_CHANGE_FIELD')} />
        <Popup.Body>
          <GridLayout className="form-horizontal">
            <GridLayout.Row className="form-group">
              <GridLayout.Col cols="3">{localization.getText('COMMON_LABEL_FIELD')}</GridLayout.Col>
              <GridLayout.Col cols="9">
                <DropDown
                  value={this.getValue()}
                  options={dataTree}
                  onChange={this.onChangeField}
                  additionalValueField="field"
                  valueField={this.getValueField}
                  labelField="name"
                  groupBy="category"
                />
              </GridLayout.Col>
            </GridLayout.Row>
            <GridLayout.Row className="form-group">
              <GridLayout.Col cols="3">{localization.getText('MODEL_LABEL_FUNCTION')}</GridLayout.Col>
              <GridLayout.Col cols="9">
                <DropDown
                  disabled={this.disableFunctions}
                  value={this.functionId}
                  options={this.functions}
                  onChange={this.onChangeFunction}
                  valueField="id"
                  labelField="name"
                />
              </GridLayout.Col>
            </GridLayout.Row>
            <GridLayout.Row className="form-group">
              <GridLayout.Col cols="3">{localization.getText('COMMON_LABEL_FORMAT')}</GridLayout.Col>
              <GridLayout.Col cols="9">
                <DropDown
                  value={this.format.formatId}
                  options={this.formats}
                  onChange={this.onChangeFormat}
                  blankOption
                  blankOptionText={OPERATOR_GROUP.UNKNOWN}
                  valueField="customId"
                  labelField="name"
                  groupBy="category"
                />
              </GridLayout.Col>
            </GridLayout.Row>
          </GridLayout>
        </Popup.Body>
        <Popup.Footer>
          <Popup.ConfirmationButton type="accept">{localization.getText('COMMON_LABEL_OK')}</Popup.ConfirmationButton>
          <Popup.ConfirmationButton type="cancel">
            {localization.getText('COMMON_LABEL_CANCEL')}
          </Popup.ConfirmationButton>
        </Popup.Footer>
      </Popup>
    );
  }
}

DynamicThresholdField.$popupName = 'DynamicThresholdField';
export default DynamicThresholdField;



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/PropertiesBuilder/ChartSettings/Izenda.DynamicThresholdField.js